import { getSignedUrls } from '../../lib/storage'
import type { Expense, FinancialTransaction, ReceiptBucket } from '../../types/financial'

// ============================================================
// RECEIPT URL RESOLUTION
// ============================================================

/**
 * The bucket a receipt lives in, as a VALUE.
 *
 * It used to be a prefix inside `receipt_url` — `issue-documents:<path>`, or a
 * bare path meaning `expense-receipts`. Migration 480 split that into
 * `receipt_storage_path` + `receipt_bucket`, so the bucket is a column rather
 * than something a parser has to recover from a string. `expense-receipts`
 * remains the default for a row that has a path but no bucket recorded.
 */
export function receiptBucketOf(bucket: ReceiptBucket | null | undefined): ReceiptBucket {
  return bucket ?? 'expense-receipts'
}

async function resolveReceiptUrlsBatch(
  items: { id: string; path: string; bucket: ReceiptBucket | null | undefined }[],
): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>()
  if (items.length === 0) return urlMap

  // Group by bucket
  const byBucket = new Map<string, { id: string; path: string }[]>()
  for (const item of items) {
    const bucket = receiptBucketOf(item.bucket)
    if (!byBucket.has(bucket)) byBucket.set(bucket, [])
    byBucket.get(bucket)!.push({ id: item.id, path: item.path })
  }

  // Resolve each bucket in parallel
  await Promise.all(
    Array.from(byBucket.entries()).map(async ([bucket, entries]) => {
      const paths = entries.map(e => e.path)
      const urls = await getSignedUrls(bucket as 'expense-receipts' | 'issue-documents', paths)
      entries.forEach((e, i) => urlMap.set(e.id, urls[i]))
    })
  )

  return urlMap
}

// `receipt_storage_path` is the stored path; `receipt_url` is the resolved
// signed URL and is never persisted (migration 480).
export async function resolveExpenseReceiptUrls(expenses: Expense[]): Promise<Expense[]> {
  const withReceipts = expenses.filter(e => e.receipt_storage_path)
  if (withReceipts.length === 0) return expenses

  const urlMap = await resolveReceiptUrlsBatch(
    withReceipts.map(e => ({ id: e.id, path: e.receipt_storage_path!, bucket: e.receipt_bucket }))
  )
  return expenses.map(e =>
    e.receipt_storage_path ? { ...e, receipt_url: urlMap.get(e.id) || '' } : e
  )
}

export async function resolveTransactionReceiptUrls(transactions: FinancialTransaction[]): Promise<FinancialTransaction[]> {
  const withReceipts = transactions.filter(t => t.receipt_storage_path)
  if (withReceipts.length === 0) return transactions

  const urlMap = await resolveReceiptUrlsBatch(
    withReceipts.map(t => ({ id: t.id, path: t.receipt_storage_path!, bucket: t.receipt_bucket }))
  )
  return transactions.map(t =>
    t.receipt_storage_path ? { ...t, receipt_url: urlMap.get(t.id) || '' } : t
  )
}
